import { type JSX, type PropsWithChildren, type RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { noop } from '@/lib/utils'
import StoreMapContext, { type StoreMapContextType, type ZoomControls } from '@components/storeMap/provider/StoreMapContext'
import usePersistedState from '@hooks/usePersistedState'
import { useSelectedDay } from '@hooks/useSelectedDay'
import { type MapOrigin } from '@state/layout/queries'
import { isDevMode, isIntegrationMode } from '@utils/env.ts'

import type { ShelfProducer } from '@components/storeMap/floor/2d/Floor'
import type { TransformedShelf } from '@state/shelf/types'

export type DimType = '2D' | '3D'

export type MapLayer = 'HIGHLIGHT_OUT_OF_SHELF' | 'HEATMAP_UNKNOWNS' | 'CATEGORY_GROUPING' | 'CATEGORY_GROUPING_DEBUG' | 'DATA_AGE'

export type ZoomLimit = 'MIN' | 'MAX'

// eslint-disable-next-line react-refresh/only-export-components
export const enabledLayerPerDimType: Record<DimType, Record<MapLayer, boolean>> = {
  '2D': {
    'CATEGORY_GROUPING': true,
    'HIGHLIGHT_OUT_OF_SHELF': true,
    'HEATMAP_UNKNOWNS': true,
    'CATEGORY_GROUPING_DEBUG': isDevMode || isIntegrationMode,
    'DATA_AGE': true,
  },
  '3D': {
    'CATEGORY_GROUPING': true,
    'HIGHLIGHT_OUT_OF_SHELF': true,
    'HEATMAP_UNKNOWNS': true,
    'CATEGORY_GROUPING_DEBUG': false,
    'DATA_AGE': true,
  },
}

interface StoreMapProviderProps extends PropsWithChildren {
  showToolbar?: boolean
  highlightedShelves?: string[]
  selectedShelfId?: string
  setSelectedShelfId?: (shelfId?: string) => void
  dimType?: DimType
  overlays2D?: JSX.Element[]
  overlays3D?: JSX.Element[]
  shelfProducer2D?: ShelfProducer
}

function StoreMapProvider({ children, highlightedShelves = [], showToolbar = false, selectedShelfId, setSelectedShelfId = noop, dimType: dimTypeProps = '2D', overlays3D, overlays2D, shelfProducer2D }: StoreMapProviderProps) {
  const { day, dayScan } = useSelectedDay()
  const [chosenMapOrigin, setMapOrigin] = useState<MapOrigin>('DEPLOY_DB')
  // one scan is what the robot saw, so it's never drawn on the deploy-db
  const mapOrigin: MapOrigin = dayScan ? 'SCAN' : chosenMapOrigin

  const [dimType, setDimType] = usePersistedState<DimType>({
    initialState: dimTypeProps,
    key: 'store-map:dim-type',
    persistenceEnabled: showToolbar,
  })

  const [activeLayers, setActiveLayers] = usePersistedState<MapLayer[]>({
    initialState: () => ['CATEGORY_GROUPING'],
    key: 'store-map:active-layer',
    version: 2,
    persistenceEnabled: showToolbar,
  })

  const [hoveredShelf, setHoveredShelf] = useState<{element: RefObject<SVGRectElement>; shelf: TransformedShelf} | undefined>(undefined)

  const zoomControls = useRef<ZoomControls | undefined>(undefined)
  const [isTransformed, setIsTransformed] = useState(false)

  const [zoomAtLimit, setZoomAtLimit] = useState<ZoomLimit>()

  function handleDimTypeChange(newDimType: DimType) {
    // reset layer state if the current active layers are not available in the new dim type
    setActiveLayers(prev => prev.filter(l => enabledLayerPerDimType[newDimType][l]))

    setDimType(newDimType)
  }

  function setActiveLayer(layer: MapLayer) {
    setActiveLayers(prev => {
      return prev.includes(layer) ? prev : [...prev, layer]
    })
  }

  function unsetActiveLayer(layer: MapLayer) {
    setActiveLayers(prev => prev.filter(l => l !== layer))
  }

  function isLayerActive(layer: MapLayer) {
    // a remembered age layer must not colour a scan view: only a day has ages
    if (layer === 'DATA_AGE' && (!day || dayScan)) return false
    return activeLayers.includes(layer)
  }

  useEffect(() => {
    if (!zoomControls.current || highlightedShelves.length == 0) return
    const firstHighlightedShelf = highlightedShelves[0]!

    zoomControls.current.zoomTo(firstHighlightedShelf)
  }, [highlightedShelves])

  const value: StoreMapContextType = {
    mapOrigin,
    setMapOrigin,
    showToolbar,
    activeLayers,
    setActiveLayer,
    unsetActiveLayer,
    isLayerActive,
    highlightedShelves,
    hoveredShelf,
    setHoveredShelf,
    dimType,
    setDimType: handleDimTypeChange,
    selectedShelfId,
    setSelectedShelfId,
    overlays2D,
    overlays3D,
    shelfProducer2D,
    registerZoomControls: useCallback((controls: ZoomControls) => {
      zoomControls.current = controls

      return () => {
        if (zoomControls.current !== controls) {
          // spicy situation: the currently registered controls are not the ones that were originally registered
          return
        }

        zoomControls.current = undefined
      }
    }, []),
    zoomToShelf: (shelfId: string) => {
      zoomControls.current?.zoomTo(shelfId)
    },
    zoomIn: () => {
      zoomControls.current?.zoomIn()
    },
    zoomOut: () => {
      zoomControls.current?.zoomOut()
    },
    resetZoom: () => {
      zoomControls.current?.resetZoom()
    },
    isTransformed,
    setIsTransformed,
    zoomAtLimit,
    setZoomAtLimit,
  }

  return (
    <StoreMapContext.Provider value={value}>
      {children}
    </StoreMapContext.Provider>
  )
}

export default StoreMapProvider